// @flow

import React from "react";
import { Label, Icon, View, DatePicker } from "@components";
import { Input } from "reactstrap";
import { ToggleButton } from "../toggle-button";
import ComboBox from "../combobox";

const FREE_TEXT = "FREE_TEXT";
const DATE_PICK = "DATE_PICK";
const SINGLE_SELECT = "SINGLE_SELECT";
const MULTI_SELECT = "MULTI_SELECT";
const CONFIRMATION = "CONFIRMATION";

export type Question = $Exact<{
  id: number,
  questionType:
    | typeof FREE_TEXT
    | typeof DATE_PICK
    | typeof SINGLE_SELECT
    | typeof MULTI_SELECT
    | typeof CONFIRMATION,
  isMandatory: boolean,
  questionText: string,
  selectableValues?: string[],
  questionLabel?: string,
  masterStandardQuestionId?: number,
  tooltip?: string,
  key?: string,
}>;

export type Props = $Exact<{
  question: Question,
  onChange: Object => void,
  answer?: string | boolean,
  value?: any,
}>;

export type State = $Exact<{
  comboBoxValue: string,
}>;

export class SingleQuestionAnswerWithConsent extends React.Component<Props, State> {
  state = {
    comboBoxValue: "",
  };

  get idName() {
    return this.props.question.questionLabel
      ? `${this.props.question.questionLabel}-${this.props.question.id}`
      : `${this.props.question.id}`;
  }

  handleChange = (value: ?string | (string | number)[]) => {
    this.props.onChange({
      [this.idName]: {
        answer: value,
        id: this.props.question.id,
      },
    });
  };

  handleInputChange = (e: Object) => {
    this.props.onChange({
      [this.idName]: {
        answer: e.target.value,
        id: this.props.question.id,
      },
    });
  };

  handleComboBoxChange = (value: string) => {
    this.setState({
      comboBoxValue: value,
    });

    this.props.onChange({
      [this.idName]: {
        answer: value,
        id: this.props.question.id,
      },
    });
  };

  renderFormField = (questionType: string) => {
    switch (questionType) {
      case DATE_PICK:
        return (
          <DatePicker
            id="render-form-field"
            onChange={this.handleChange}
            value={this.props.value || ""}
          />
        );
      case FREE_TEXT:
        return (
          <Input
            type="textarea"
            onChange={this.handleInputChange}
            defaultValue={this.props.value}
          />
        );
      case SINGLE_SELECT:
        return (
          <ComboBox
            className="w-100"
            onChange={this.handleComboBoxChange}
            options={
              this.props.question.selectableValues
                ? this.props.question.selectableValues.map(value => ({ value, label: value }))
                : []
            }
            value={this.state.comboBoxValue || this.props.value}
          />
        );
      case MULTI_SELECT:
        return (
          <ComboBox
            onChange={data => this.handleChange(data.length ? data : [])}
            options={
              this.props.question.selectableValues ? this.props.question.selectableValues : null
            }
            labelKey="label"
            isMulti={true}
            className="multiple-typeahead-select"
            value={this.props.value}
          />
        );
      case CONFIRMATION:
        return (
          <ToggleButton
            onChange={this.handleChange}
            items={[
              {
                label: "Yes",
                value: true,
                active: this.props.value === true,
              },
              {
                label: "No",
                value: false,
                active: this.props.value === false,
              },
            ]}
          />
        );
      default:
        return "";
    }
  };

  render() {
    const { question } = this.props;
    return (
      question.questionType && (
        <View direction="row">
          <View flex={1} margin="0 2rem 0 0" className="overflow-hidden">
            <Label className="font-size-14 font-weight-medium">
              {question.questionText}
              {question.isMandatory && (
                <Icon icon="asterisk" size="xs" className="text-warning ml-2" />
              )}
            </Label>
          </View>
          <View flex={2}>{this.renderFormField(question.questionType)}</View>
        </View>
      )
    );
  }
}

export default SingleQuestionAnswerWithConsent;
